import { useTheme } from '@mui/material';
import React, { PropsWithChildren } from 'react';

import { List, ListItem, ListItemAvatar, ListItemText, ListProps } from '../../components/List/List';
import { TooltipButton } from '../../components/TooltipButton/TooltipButton';
import { Typography } from '../../components/Typography/Typography';
import type { Theme } from '../../themes/themes';
import { slugify } from '../../utilities/slugify';
import { getStyles } from './BenefitsList.styles';

interface BenefitsListHeadlineProps {
  children: string;
}

export const BenefitsListHeadline: React.FC<BenefitsListHeadlineProps> = ({ children }) => {
  return (
    <Typography variant="h3" innerPadding="horizontal" id={slugify(children)}>
      {children}
    </Typography>
  );
};

interface BenefitsListProps extends ListProps {
  headlineTitle?: string;
  variant: 'icon' | 'number';
}

export const BenefitsList: React.FC<PropsWithChildren<BenefitsListProps>> = ({
  headlineTitle,
  variant,
  children,
  size = 'medium',
  ...props
}: BenefitsListProps) => {
  return (
    <>
      {headlineTitle && <BenefitsListHeadline>{headlineTitle}</BenefitsListHeadline>}
      <List {...props} size={size} variant={variant} disablePadding>
        {React.Children.map(children, (item: React.ReactElement) => (
          <ListItem key={item.key}>{item}</ListItem>
        ))}
      </List>
    </>
  );
};

interface BenefitsListItemTextProps {
  tooltip?: React.ReactNode;
  icon?: React.ReactNode;
  headline?: string;
  children?: React.ReactNode;
}

export const BenefitsListItemText: React.FC<BenefitsListItemTextProps> = ({
  tooltip,
  icon,
  headline,
  children,
}: BenefitsListItemTextProps) => {
  const theme = useTheme<Theme>();
  const styles = getStyles(theme);
  return (
    <>
      {icon ? <ListItemAvatar sx={styles.icon}>{icon}</ListItemAvatar> : null}
      <ListItemText primary={headline ? headline : null} secondary={children} sx={styles.text} />
      {tooltip ? <TooltipButton title={tooltip} sx={styles.tooltip} placement="top-start" /> : null}
    </>
  );
};
